import React from "react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  Callout, Checkbox, FieldGroup, FormField, Input, Radio,
} from "../../ui";
import SectionMark from "./SectionMark";
import { isPublicOnly, letterOf, paperSummary, timedSections } from "./builderRules";

const QUICK_MINUTES = [15, 30, 45, 60, 90, 120];
const QUICK_PENALTIES = [0.25, 0.33, 0.5, 1];

const num = (v) => (v === "" ? "" : Number(v));

/**
 * Step 4 — the rules: the clock, the marking, what students see afterwards and
 * how the exam window is guarded.
 *
 * Timed sections in order are offered only when there are two or more
 * sections; each then needs its own minutes, and the total is worked out.
 *
 * A paper for public exams keeps only what the paper itself decides — the
 * clock and the marking. Attempts, what candidates see afterwards and exam
 * security belong to the public exam, so they are not asked twice.
 */
export default function RulesStep({ form, set, setForm, errors, locked, codingShown }) {
  const publicOnly = isPublicOnly(form);
  const { totals } = paperSummary(form);
  // Where Coding Practice is off, the rules say nothing about coding answers.
  const mentionCoding = codingShown || totals.coding > 0;
  const multi = form.sectioned && form.sections.length > 1;
  const perSection = timedSections(form);
  const ac = form.antiCheat;
  const setAc = (patch) => setForm((f) => ({ ...f, antiCheat: { ...f.antiCheat, ...patch } }));
  const first = form.sections[0];
  const poolable = !form.sectioned && first?.source === "pick" && (first.questions || []).length > 1
    && !(first.coding || []).length;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Time</AppCardTitle>
          <AppCardDescription>The server keeps the clock — a student's own clock or a refresh changes nothing.</AppCardDescription>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
          {multi && (
            <FieldGroup legend="How the time works">
              <div className="tw-grid tw-gap-3 min-[768px]:tw-grid-cols-2">
                <Radio
                  variant="card"
                  name="timing"
                  checked={!perSection}
                  onChange={() => setForm((f) => ({
                    ...f,
                    timing: "overall",
                    // Carry the sections' total across, so switching loses no time.
                    timeLimit: Number(f.timeLimit) > 0 ? f.timeLimit
                      : f.sections.reduce((sum, s) => sum + (Number(s.timeLimit) || 0), 0),
                  }))}
                  disabled={locked}
                  label="One timer for the whole assessment"
                  description="Students move between sections freely, in any order."
                />
                <Radio
                  variant="card"
                  name="timing"
                  checked={perSection}
                  onChange={() => setForm((f) => {
                    // Untimed sections start with a fair share of the overall time.
                    const share = Number(f.timeLimit) > 0 ? Math.max(1, Math.round(f.timeLimit / f.sections.length)) : 20;
                    return {
                      ...f,
                      timing: "sections",
                      sections: f.sections.map((s) => (Number(s.timeLimit) > 0 ? s : { ...s, timeLimit: share })),
                    };
                  })}
                  disabled={locked}
                  label="A timer for each section"
                  description="Sections run in order, like a placement test. A finished section cannot be reopened."
                />
              </div>
            </FieldGroup>
          )}

          {perSection ? (
            <div className="tw-flex tw-flex-col tw-gap-2">
              {form.sections.map((s, i) => (
                <div key={s.key} className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line tw-px-3 tw-py-2">
                  <SectionMark letter={letterOf(i)} size="sm" />
                  <span className="tw-min-w-[10rem] tw-flex-1 tw-text-sm tw-text-content">{s.title?.trim() || `Section ${letterOf(i)}`}</span>
                  <span className="tw-w-36">
                    <Input
                      type="number"
                      inputMode="numeric"
                      size="sm"
                      min={1}
                      aria-label={`Minutes for ${s.title?.trim() || `Section ${letterOf(i)}`}`}
                      id={`time-${s.key}`}
                      value={s.timeLimit || ""}
                      invalid={!!errors[`time-${s.key}`]}
                      onChange={(e) => setForm((f) => ({
                        ...f,
                        sections: f.sections.map((x) => (x.key === s.key ? { ...x, timeLimit: num(e.target.value) } : x)),
                      }))}
                      trailing={<span className="tw-text-xs">min</span>}
                      trailingPad="text"
                      disabled={locked}
                    />
                  </span>
                </div>
              ))}
              {form.sections.map((s) => errors[`time-${s.key}`] && (
                <p key={s.key} className="tw-m-0 tw-text-xs tw-text-danger" role="alert">{errors[`time-${s.key}`]}</p>
              ))}
              <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">
                The whole assessment takes <strong className="tw-text-content">{totals.minutes} minutes</strong>.
              </p>
            </div>
          ) : (
            <div className="tw-flex tw-flex-col tw-gap-2">
              <FormField label="Time limit" hint="Leave at 0 for no time limit.">
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  size="lg"
                  value={form.timeLimit}
                  onChange={(e) => set("timeLimit", num(e.target.value))}
                  trailing={<span className="tw-text-[0.8125rem]">minutes</span>}
                  trailingPad="text"
                  disabled={locked}
                  className="tw-max-w-xs"
                />
              </FormField>
              <div className="tw-flex tw-flex-wrap tw-gap-1.5" role="group" aria-label="Quick time limits">
                {QUICK_MINUTES.map((m) => (
                  <AppButton key={m} type="button" size="xs" variant={Number(form.timeLimit) === m ? "outline-primary" : "outline"} onClick={() => set("timeLimit", m)} disabled={locked}>
                    {m} min
                  </AppButton>
                ))}
                <AppButton type="button" size="xs" variant={!Number(form.timeLimit) ? "outline-primary" : "outline"} onClick={() => set("timeLimit", 0)} disabled={locked}>
                  No limit
                </AppButton>
              </div>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Marking</AppCardTitle>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-grid tw-gap-4 min-[768px]:tw-grid-cols-2">
            <FormField
              label="Pass mark"
              error={errors.passingPercentage}
              hint={publicOnly ? "0 means there is no pass mark. A public exam can set its own." : "0 means there is no pass mark."}
            >
              <Input
                type="number"
                inputMode="decimal"
                min={0}
                max={100}
                size="lg"
                value={form.passingPercentage}
                onChange={(e) => set("passingPercentage", num(e.target.value))}
                trailing={<span className="tw-text-[0.8125rem]">%</span>}
                disabled={locked}
              />
            </FormField>
            {!publicOnly && (
              <FormField label="Attempts allowed" error={errors.maxAttempts} hint="0 means students may take it as often as they like.">
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  size="lg"
                  value={form.maxAttempts}
                  onChange={(e) => set("maxAttempts", num(e.target.value))}
                />
              </FormField>
            )}
          </div>

          <Checkbox
            variant="card"
            checked={form.negativeMarking}
            onChange={(e) => setForm((f) => ({
              ...f,
              negativeMarking: e.target.checked,
              negativeMarkValue: e.target.checked && !(Number(f.negativeMarkValue) > 0) ? 0.25 : f.negativeMarkValue,
            }))}
            disabled={locked}
            label="Negative marking"
            description={`A wrong answer loses marks; an unanswered one loses nothing.${mentionCoding ? " Never applied to coding problems." : ""}`}
          />
          {form.negativeMarking && (
            <div className="tw-flex tw-flex-col tw-gap-2 tw-pl-1">
              <FormField label="Marks lost per wrong answer" error={errors.negativeMarkValue} hint="Used for any question that does not set its own penalty.">
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={0.25}
                  value={form.negativeMarkValue}
                  onChange={(e) => set("negativeMarkValue", num(e.target.value))}
                  disabled={locked}
                  className="tw-max-w-[10rem]"
                />
              </FormField>
              <div className="tw-flex tw-flex-wrap tw-gap-1.5" role="group" aria-label="Common penalties">
                {QUICK_PENALTIES.map((p) => (
                  <AppButton key={p} type="button" size="xs" variant={Number(form.negativeMarkValue) === p ? "outline-primary" : "outline"} onClick={() => set("negativeMarkValue", p)} disabled={locked}>
                    −{p}
                  </AppButton>
                ))}
              </div>
            </div>
          )}

          <Checkbox
            variant="card"
            checked={form.partialMarking}
            onChange={(e) => set("partialMarking", e.target.checked)}
            disabled={locked}
            label="Partial credit on multiple-answer questions"
            description="Each right option earns its share of the marks instead of all-or-nothing."
          />

          <div className="tw-grid tw-gap-3 min-[768px]:tw-grid-cols-2">
            <Checkbox
              variant="card"
              checked={form.shuffleQuestions}
              onChange={(e) => set("shuffleQuestions", e.target.checked)}
              disabled={locked}
              label="Shuffle the questions"
              description="Each student sees them in a different order — inside each section."
            />
            <Checkbox
              variant="card"
              checked={form.shuffleOptions}
              onChange={(e) => set("shuffleOptions", e.target.checked)}
              disabled={locked}
              label="Shuffle the options"
              description="A, B, C and D come in a different order for each student."
            />
          </div>

          {poolable && (
            <div className="tw-flex tw-flex-col tw-gap-2">
              <Checkbox
                variant="card"
                checked={form.deliverCount > 0}
                onChange={(e) => set("deliverCount", e.target.checked ? Math.max(1, Math.ceil(first.questions.length / 2)) : 0)}
                disabled={locked}
                label="Give each student a random subset"
                description="Students get different papers drawn from the questions you picked."
              />
              {form.deliverCount > 0 && (
                <FormField label="Questions per student" hint={`Out of the ${first.questions.length} you picked.`}>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={first.questions.length}
                    value={form.deliverCount}
                    onChange={(e) => set("deliverCount", Math.max(1, Math.min(first.questions.length, Number(e.target.value) || 1)))}
                    disabled={locked}
                    className="tw-max-w-[10rem]"
                  />
                </FormField>
              )}
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {publicOnly ? (
        <Callout tone="info">
          Who can enter, exam security (full screen, tab switching, webcam) and what candidates see after they
          submit are set on the public exam — you will choose them there.
        </Callout>
      ) : (
        <>
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>After submitting</AppCardTitle>
              <AppCardDescription>Students always see their score. These decide what else they see.</AppCardDescription>
            </AppCardHeader>
            <AppCardContent className="tw-flex tw-flex-col tw-gap-3">
              <Checkbox
                variant="card"
                checked={form.allowReview}
                onChange={(e) => set("allowReview", e.target.checked)}
                disabled={locked}
                label="Let students review their answers"
                description={`Question by question, with what they chose.${mentionCoding ? " Coding answers show their code and which tests passed." : ""}`}
              />
              <div className="tw-grid tw-gap-3 min-[768px]:tw-grid-cols-2">
                <Checkbox
                  variant="card"
                  checked={form.allowReview && form.showCorrectAnswers}
                  onChange={(e) => set("showCorrectAnswers", e.target.checked)}
                  disabled={locked || !form.allowReview}
                  label="Show the correct answers"
                  description="In the review. Leave off for a paper you will use again."
                />
                <Checkbox
                  variant="card"
                  checked={form.allowReview && form.showExplanation}
                  onChange={(e) => set("showExplanation", e.target.checked)}
                  disabled={locked || !form.allowReview}
                  label="Show explanations"
                  description="Where a question has one."
                />
              </div>
            </AppCardContent>
          </AppCard>

          <AppCard>
            <AppCardHeader>
              <AppCardTitle>Exam security</AppCardTitle>
              <AppCardDescription>Recorded for you to review. A public exam link uses the drive's own security settings instead.</AppCardDescription>
            </AppCardHeader>
            <AppCardContent className="tw-flex tw-flex-col tw-gap-3">
              <Checkbox
                variant="card"
                checked={ac.tabSwitchDetection}
                onChange={(e) => setAc({ tabSwitchDetection: e.target.checked })}
                disabled={locked}
                label="Watch for tab switching"
                description="Leaving the exam window is counted and the student is warned."
              />
              {ac.tabSwitchDetection && (
                <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-4 tw-pl-1">
                  <FormField label="Switches allowed">
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={ac.maxTabSwitches}
                      onChange={(e) => setAc({ maxTabSwitches: Math.max(0, Number.parseInt(e.target.value, 10) || 0) })}
                      disabled={locked}
                      className="tw-max-w-[8rem]"
                    />
                  </FormField>
                  <Checkbox
                    checked={ac.autoSubmitOnViolation}
                    onChange={(e) => setAc({ autoSubmitOnViolation: e.target.checked })}
                    disabled={locked}
                    label="Submit the exam automatically after that"
                    description="What they answered is still marked."
                  />
                </div>
              )}
              <div className="tw-grid tw-gap-3 min-[768px]:tw-grid-cols-2">
                <Checkbox
                  variant="card"
                  checked={ac.fullscreenRequired}
                  onChange={(e) => setAc({ fullscreenRequired: e.target.checked })}
                  disabled={locked}
                  label="Require full screen"
                  description="The paper is hidden while they are out of it."
                />
                <Checkbox
                  variant="card"
                  checked={ac.disableCopyPaste}
                  onChange={(e) => setAc({ disableCopyPaste: e.target.checked })}
                  disabled={locked}
                  label="Block copy and paste"
                  description={totals.coding > 0 ? "Includes pasting into the code editor." : "Text cannot be copied out or pasted in."}
                />
                <Checkbox
                  variant="card"
                  checked={ac.disableRightClick}
                  onChange={(e) => setAc({ disableRightClick: e.target.checked })}
                  disabled={locked}
                  label="Block right-click"
                />
              </div>
            </AppCardContent>
          </AppCard>
        </>
      )}
    </div>
  );
}
